import { useCallback, useEffect, useState } from 'react';
import { isApiError } from '@aiptyg/types';
import * as legalService from '../services/legal.service';
import type { LegalDocumentDTO, LegalDocumentSummaryDTO } from '../types/legal.types';

const BASE_DOCUMENTS: LegalDocumentSummaryDTO[] = [
  {
    slug: 'terms-of-service',
    kind: 'terms_of_service',
    title: '服务条款',
    summary: '当前地区服务条款',
    effective_date: '',
    updated_at: '',
    detail_path: '/v1/public/legal-documents/terms-of-service',
    markdown_path: '/v1/public/legal-documents/terms-of-service/markdown',
  },
  {
    slug: 'privacy-policy',
    kind: 'privacy_policy',
    title: '隐私协议',
    summary: '当前地区隐私协议',
    effective_date: '',
    updated_at: '',
    detail_path: '/v1/public/legal-documents/privacy-policy',
    markdown_path: '/v1/public/legal-documents/privacy-policy/markdown',
  },
];

function resolveError(error: unknown): string {
  if (!isApiError(error)) {
    return '未知错误';
  }

  const map: Record<string, string> = {
    network_error: '网络连接失败，请检查网络',
    invalid_response: '接口返回格式不正确',
    'legal document not found': '法律文档不存在',
  };

  return map[error.message] ?? `请求失败 (${error.message})`;
}

export function useLegalDocuments(defaultSlug = 'terms-of-service') {
  const [items, setItems] = useState<LegalDocumentSummaryDTO[]>([]);
  const [selectedSlug, setSelectedSlug] = useState(defaultSlug);
  const [selectedMarketId, setSelectedMarketId] = useState('CN');
  const [selectedDocument, setSelectedDocument] = useState<LegalDocumentDTO | null>(null);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadList = useCallback(async () => {
    setLoadingList(true);
    setError(null);
    try {
      const result = await legalService.listPublicLegalDocuments(selectedMarketId);
      const runtimeItems = result.items ?? [];
      const nextItems = mergeLegalDocumentOptions(runtimeItems, selectedMarketId);
      setItems(nextItems);

      if (nextItems.length === 0) {
        setSelectedSlug('');
        return;
      }

      const hasSelected = nextItems.some((item) => item.slug === selectedSlug);
      if (!hasSelected) {
        setSelectedSlug(nextItems[0].slug);
      }
    } catch (e) {
      setError(resolveError(e));
    } finally {
      setLoadingList(false);
    }
  }, [selectedMarketId, selectedSlug]);

  const loadDetail = useCallback(async (slug: string) => {
    if (!slug) {
      setSelectedDocument(null);
      setLoadingDetail(false);
      return;
    }

    setLoadingDetail(true);
    setError(null);
    try {
      const result = await legalService.getPublicLegalDocument(slug, selectedMarketId);
      setSelectedDocument({ ...result, market_id: selectedMarketId });
    } catch (e) {
      if (selectedMarketId === 'CN') {
        setError(resolveError(e));
        setSelectedDocument(null);
      } else {
        setSelectedDocument(createBlankDocument(slug, selectedMarketId));
      }
    } finally {
      setLoadingDetail(false);
    }
  }, [selectedMarketId]);

  useEffect(() => {
    void loadList();
  }, [loadList]);

  useEffect(() => {
    void loadDetail(selectedSlug);
  }, [loadDetail, selectedSlug]);

  const reload = useCallback(async () => {
    await loadList();
    await loadDetail(selectedSlug);
  }, [loadDetail, loadList, selectedSlug]);

  const selectMarket = useCallback((marketId: string) => {
    setSelectedMarketId(marketId.trim() || 'CN');
  }, []);

  return {
    items,
    selectedSlug,
    selectedMarketId,
    selectedDocument,
    loading: loadingList || loadingDetail,
    loadingList,
    loadingDetail,
    error,
    setSelectedSlug,
    selectMarket,
    reload,
  };
}

function mergeLegalDocumentOptions(runtimeItems: LegalDocumentSummaryDTO[], marketId: string): LegalDocumentSummaryDTO[] {
  const runtimeBySlug = new Map(runtimeItems.map((item) => [item.slug, item]));
  return BASE_DOCUMENTS.map((base) => ({
    ...base,
    ...runtimeBySlug.get(base.slug),
    market_id: marketId,
  }));
}

function createBlankDocument(slug: string, marketId: string): LegalDocumentDTO | null {
  const base = BASE_DOCUMENTS.find((item) => item.slug === slug);
  if (!base) return null;
  return {
    ...base,
    market_id: marketId,
    title: '',
    summary: '',
    effective_date: '',
    updated_at: '',
    content_markdown: '',
    content_html: '',
  };
}
